import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, ArrowUpRight, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/password-reset-request", { email });
      setSent(true);
      toast.success("Check your inbox for the reset link.");
    } catch {
      toast.error("Could not send reset email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="forgot-password-page">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-ink text-white relative overflow-hidden grain">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-white text-ink grid place-items-center"><Sparkles size={16} /></div>
          <span className="font-display font-bold text-lg">Zenith Ranking</span>
        </Link>
        <div>
          <div className="eyebrow !text-white/50 mb-3">Account recovery</div>
          <h2 className="font-display text-4xl font-bold tracking-tight">One quick email and you'll be back inside.</h2>
        </div>
        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-terra/30 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="eyebrow mb-3">Forgot password</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Reset it in one step</h1>
          <p className="mt-2 text-sm text-inkSoft">Enter the email you signed up with — we'll send a link that expires in 30 minutes.</p>

          {sent ? (
            <div className="mt-8 rounded-2xl border border-terra/30 bg-terra/5 p-5" data-testid="reset-sent-confirmation">
              <CheckCircle2 className="text-terra" />
              <div className="mt-2 font-medium">Check your inbox</div>
              <div className="text-sm text-inkSoft mt-1">If an account exists for <span className="font-mono">{email}</span>, a reset link is on its way. Also check spam.</div>
              <Link to="/login" className="btn-primary text-sm mt-4 w-full justify-center">Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-3" data-testid="forgot-form">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-inkSoft">Email</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 w-full h-11 px-4 rounded-xl border border-line bg-white text-sm outline-none focus:border-ink"
                  data-testid="forgot-email"
                />
              </label>
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center mt-2" data-testid="forgot-submit">
                {loading ? "Sending…" : (<>Send reset link <ArrowUpRight size={16} /></>)}
              </button>
              <p className="text-xs text-inkSoft text-center">
                Remembered it? <Link to="/login" className="text-terra hover:underline">Sign in</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
